import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '../../hooks/useQuery';
import type { Set as MTGSet } from '../../types/mtg';
import { CardGrid } from '../../components/cards/CardGrid';
import { FilterBar } from '../../components/filters/FilterBar';
import { Library, Loader2, Trophy, Check, Plus, Minus, Percent } from 'lucide-react';
import { useCards } from '../../hooks/useCards';
import { useSetCards } from '../../hooks/useSetCards';
import { useSetFilterOptions } from '../../hooks/useSetFilterOptions';
import { FilterState } from '../../types/pokemon';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { useCollection } from '../../hooks/useCollection';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { SetStatistics } from '../../components/stats/SetStatistics';
import { PageTitle } from '../../components/layout/PageTitle';
import { Tabs, Button, InputNumber, message, Skeleton } from 'antd';
import { supabase } from '../../lib/supabase';
import { PokemonCard, UserCard } from '../../types/pokemon';
import { useMissionProgress } from '../../hooks/useMissionProgress';
import { PageLayout } from '../../components/layout/PageLayout';
import { useTradeSettings } from '../../hooks/useTradeSettings';
import { usePackProbabilities } from '../../hooks/usePackProbabilities';
import { PackProbabilities } from '../../components/stats/PackProbabilities';
import { AdSense } from '../../components/ads/AdSense';
import { useTranslation } from '../../hooks/useTranslation';
import { translateEnergyType } from '../../utils/energyUtils';

interface SetStatisticsData {
  total_cards: number;
  rarity_distribution: Record<string, number>;
}

interface MissionCardGroup {
  id: string;
  mission_card_variants: {
    card_id: number;
    cards: PokemonCard;
  }[];
}

interface MissionReward {
  quantity: number;
  rewards: {
    id: string;
    name: string;
    image_url: string;
  };
}

interface SupabaseMission {
  id: string;
  name: string;
  description: string;
  mission_card_groups: MissionCardGroup[];
  mission_rewards: MissionReward[];
}

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  details?: {
    quantity: number;
    selectedCount: number;
  };
}

interface TradingRule {
  id: string;
  eligible_sets: string[];
  eligible_rarities: string[];
  created_at: string;
  updated_at: string;
}

interface PackInfo {
  name: string;
  img: string | null;
}

const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, details }: ConfirmModalProps) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-500 mb-4">{message}</p>
        {details && (
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <p className="text-sm text-gray-600">
              • {t('set_detail.bulk_mode.set_quantity')} <span className="font-medium text-gray-900">{details.quantity}</span>
            </p>
            <p className="text-sm text-gray-600 mt-1">
              • {t('set_detail.bulk_mode.for')} <span className="font-medium text-gray-900">{details.selectedCount}</span> {t('set_detail.bulk_mode.selected_cards', { count: details.selectedCount })}
            </p>
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
          >
            {t('set_detail.bulk_mode.cancel')}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
          >
            {t('set_detail.bulk_mode.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

const SetDetailSkeleton = () => {
  return (
    <div className="px-2 sm:px-4">
      <div className="mt-2 sm:mt-4 bg-white rounded-xl p-2 sm:p-3 md:p-4 shadow-md">
        <div className="flex items-center gap-3 sm:gap-4">
          <Skeleton.Image active className="!w-16 !h-16 sm:!w-20 sm:!h-20" />
          <div className="flex-1">
            <Skeleton.Input active size="large" className="!w-[200px]" />
            <div className="mt-2">
              <Skeleton.Input active size="small" className="!w-[100px]" />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 sm:mt-4">
        <div className="bg-gray-50 rounded-lg shadow-sm px-2 sm:px-3 md:px-4 py-3 sm:py-4">
          <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-3 xl:grid-cols-4 lg:gap-6 xl:gap-8">
            <div className="lg:col-span-1 order-first lg:order-last">
              <div className="bg-white rounded-lg p-4 shadow">
                <Skeleton active paragraph={{ rows: 4 }} />
              </div>
            </div>

            <div className="lg:col-span-2 xl:col-span-3 order-last lg:order-first">
              <div className="bg-white rounded-lg p-4 shadow mb-4">
                <Skeleton active paragraph={{ rows: 1 }} />
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4">
                {[...Array(8)].map((_, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 shadow">
                    <Skeleton.Image active className="!w-full !h-[200px]" />
                    <Skeleton active paragraph={{ rows: 1 }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export function SetDetail() {
  const navigate = useNavigate();
  const { code } = useParams<{ code: string }>();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tabFromUrl = searchParams.get('tab');
  const { t, lang, getPathForLang } = useTranslation();
  const { data: set, loading: setLoading, error: setError } = useQuery<MTGSet>('sets', { name: code || null }, { single: true });
  const { data: setStats } = useQuery<SetStatisticsData>('set_statistics', { set: code || null }, { single: true });
  const { packs, rarities, loading: filterOptionsLoading, types, energies } = useSetFilterOptions(code || null);
  const [filters, setFilters] = useState<FilterState>({
    pack: null,
    rarity: null,
    set: code || null,
    search: '',
    showMissing: false,
    type: null,
    energy: null
  });

  // État pour la modification en masse
  const [selectedCards, setSelectedCards] = useState(new Set<number>());
  const [bulkQuantity, setBulkQuantity] = useState<number>(0);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);

  // État pour les missions
  const [missions, setMissions] = useState<SupabaseMission[]>([]);
  const [missionsLoading, setMissionsLoading] = useState(true);

  const { cards: allSetCards, loading: allSetCardsLoading } = useSetCards(code);
  
  const {
    cards: filteredCards,
    loading: cardsLoading,
    error: cardsError,
    hasMore,
    isLoadingMore,
    loadMore
  } = useCards(filters);

  const { collection, updateCardQuantity, updateCardWanted, updateCardTradable, setCollection } = useCollection();
  const { calculateProgress } = useMissionProgress();
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'cards');
  const { data: tradeSettings } = useTradeSettings();

  const { calculatePackProbabilities } = usePackProbabilities(code || undefined);
  const packProbabilities = useMemo(() => {
    const result = calculatePackProbabilities(allSetCards, collection);
    return result;
  }, [allSetCards, collection, calculatePackProbabilities]);

  // Fonction pour vérifier si une carte est éligible à l'échange
  const isCardTradeable = (rarity: string) => {
    if (!tradeSettings?.[0]) {
      return false;
    }
    
    const tradingRules = tradeSettings[0];
    const isEligible = (
      tradingRules.eligible_sets.includes(code || '') && 
      tradingRules.eligible_rarities.includes(rarity)
    );
    
    return isEligible;
  };

  // Mettre à jour l'URL quand l'onglet change
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    const newSearchParams = new URLSearchParams(location.search);
    if (newTab === 'cards') {
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', newTab);
    }
    navigate(`${location.pathname}?${newSearchParams.toString()}`);
  };

  // Déclarer les fonctions avant leur utilisation
  const handleCardClick = (card: PokemonCard) => {
    if (isBulkMode) {
      // En mode sélection, ajouter/retirer la carte de la sélection
      const newSelectedCards = new Set(selectedCards);
      if (newSelectedCards.has(card.id)) {
        newSelectedCards.delete(card.id);
      } else {
        newSelectedCards.add(card.id);
      }
      setSelectedCards(newSelectedCards);
    } else {
      // En mode normal, naviguer vers la page de détail de la carte
      const cardPath = getPathForLang(`/sets/${code}/cards/${card.id}`);
      navigate(cardPath);
    }
  };

  const handleSelectAll = () => {
    if (selectedCards.size === allSetCards.length) {
      // Si toutes les cartes sont sélectionnées, désélectionner tout
      setSelectedCards(new Set());
    } else {
      // Sinon, sélectionner toutes les cartes
      const allIds = new Set(allSetCards.map(card => card.id));
      setSelectedCards(allIds);
    }
  };

  const showConfirmModal = () => {
    setIsConfirmModalVisible(true);
  };

  const handleBulkUpdate = async () => {
    setIsConfirmModalVisible(false);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        message.error(t('set_detail.error.user_not_logged_in'));
        return;
      }

      const updates = Array.from(selectedCards).map(cardId => ({
        card_id: cardId,
        quantity: bulkQuantity
      }));
      
      // Mettre à jour la base de données
      await supabase
        .from('user_cards')
        .upsert(
          updates.map(update => ({
            user_id: user.id,
            card_id: update.card_id,
            quantity: update.quantity
          })),
          { onConflict: 'user_id, card_id' }
        );
      
      // Mettre à jour la collection locale
      setCollection((prev: Record<number, UserCard>) => {
        const newCollection = { ...prev };
        
        updates.forEach(update => {
          if (update.quantity <= 0) {
            // Si la quantité est 0 ou moins, supprimer la carte de la collection
            delete newCollection[update.card_id];
          } else {
            // Sinon, ajouter ou mettre à jour la carte dans la collection
            newCollection[update.card_id] = {
              ...(prev[update.card_id] || { card_id: update.card_id, wanted: false, tradable: false }),
              quantity: update.quantity
            };
          }
        });
        
        return newCollection;
      });
      
      // Réinitialiser l'état
      setSelectedCards(new Set());
      setBulkQuantity(0);
      setIsBulkMode(false);
      
      message.success(t('set_detail.bulk_mode.success', { count: updates.length }));
    } catch (error) {
      console.error('Error updating cards:', error);
      message.error(t('set_detail.bulk_mode.error'));
    }
  };

  const handleWantedToggle = async (cardId: number, currentValue: boolean) => {
    try {
      await updateCardWanted(cardId, !currentValue);
    } catch (error) {
      message.error('Erreur lors de la mise à jour de la carte');
    }
  };

  const handleTradableToggle = async (cardId: number, currentValue: boolean) => {
    try {
      await updateCardTradable(cardId, !currentValue);
    } catch (error) {
      message.error('Erreur lors de la mise à jour de la carte');
    }
  };

  const handleQuantityChange = async (cardId: number, newQuantity: number) => {
    try {
      await updateCardQuantity(cardId, newQuantity);
    } catch (error) {
      message.error('Erreur lors de la mise à jour de la quantité');
    }
  };

  // Définir les onglets disponibles
  const tabItems = [
    {
      key: 'cards',
      label: (
        <div className="flex items-center gap-2 px-3 py-2 text-base">
          <Library className="w-5 h-5" />
          <span>{t('set_detail.tabs.cards')}</span>
        </div>
      ),
      children: (
        <div className="bg-gray-50 rounded-lg rounded-tl-none shadow-sm px-2 sm:px-3 md:px-4 py-3 sm:py-4">
          <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-3 xl:grid-cols-4 lg:gap-6 xl:gap-8">
            <div className="lg:col-span-1 order-first lg:order-last">
              <SetStatistics cards={allSetCards} collection={collection} setCode={code} />
            </div>
            <div className="lg:col-span-2 xl:col-span-3 order-last lg:order-first">
              <FilterBar
                filters={filters}
                onFilterChange={setFilters}
                packs={packs}
                rarities={rarities}
                types={types}
                energies={energies}
                sets={[]}
                className="mx-0"
              />
              
              {/* Annonce AdSense en haut de la liste des cartes */}
              <div className="mt-6 mb-8 flex justify-center items-center">
                <AdSense adSlot="1424794127" id="cards-top" />
              </div>
              
              <div className="mt-3 sm:mt-4 bg-white rounded-lg shadow p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <h3 className="text-base font-medium text-gray-900">{t('set_detail.bulk_actions.title')}</h3>
                    {isBulkMode && (
                      <div className="flex items-center gap-2 bg-gray-50 px-2 py-1 rounded">
                        <span className="text-sm font-medium text-gray-900">
                          {t('set_detail.bulk_actions.selected_cards', { count: selectedCards.size })} / {allSetCards.length}
                        </span>
                      </div>
                    )}
                  </div>
                  <Button
                    type={isBulkMode ? "primary" : "default"}
                    className={`w-full sm:w-auto ${isBulkMode ? "!bg-indigo-600 hover:!bg-indigo-700 !border-transparent" : ""}`}
                    onClick={() => {
                      if (isBulkMode) {
                        setSelectedCards(new Set());
                        setBulkQuantity(0);
                      }
                      setIsBulkMode(!isBulkMode);
                    }}
                  >
                    {isBulkMode ? t('set_detail.bulk_mode.exit') : t('set_detail.bulk_mode.enter')}
                  </Button>
                </div>

                {isBulkMode && (
                  <div className="mt-4">
                    <div className="flex flex-col gap-4 bg-gray-50 px-4 py-3 rounded-lg">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <Button 
                          type="primary"
                          className="w-full sm:w-auto !bg-indigo-600 hover:!bg-indigo-700 !border-transparent"
                          onClick={handleSelectAll}
                        >
                          {selectedCards.size === allSetCards.length ? t('set_detail.bulk_actions.clear_selection') : t('set_detail.bulk_actions.select_all')}
                        </Button>

                        <div className="w-full sm:w-auto flex flex-col sm:flex-row items-start sm:items-center gap-2">
                          <span className="text-sm text-gray-500">{t('set_detail.card.quantity')}:</span>
                          <div className="flex items-center w-full sm:w-auto">
                            <Button
                              type="default"
                              className="flex items-center justify-center w-8 h-8 p-0 !bg-indigo-600 !text-white hover:!bg-indigo-700 !border-transparent"
                              onClick={() => setBulkQuantity(Math.max(0, (bulkQuantity || 0) - 1))}
                              disabled={bulkQuantity === 0}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <InputNumber
                              min={0}
                              value={bulkQuantity}
                              onChange={(value: number | null) => setBulkQuantity(value || 0)}
                              placeholder="0"
                              className="w-full sm:w-16 mx-2 [&_.ant-input-number-input]:text-center"
                              controls={false}
                            />
                            <Button
                              type="default"
                              className="flex items-center justify-center w-8 h-8 p-0 !bg-indigo-600 !text-white hover:!bg-indigo-700 !border-transparent"
                              onClick={() => setBulkQuantity((bulkQuantity || 0) + 1)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>

                      <Button
                        type="primary"
                        className={`w-full sm:w-auto !border-transparent ${
                          selectedCards.size === 0
                            ? "!bg-gray-200 hover:!bg-gray-200 !text-gray-400 cursor-not-allowed"
                            : "!bg-green-600 hover:!bg-green-700"
                        }`}
                        icon={<Check className="w-4 h-4" />}
                        onClick={showConfirmModal}
                        disabled={selectedCards.size === 0}
                      >
                        {t('set_detail.bulk_mode.update')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-3 sm:mt-6">
                <CardGrid
                  cards={filteredCards}
                  loading={cardsLoading}
                  collection={collection}
                  onQuantityChange={handleQuantityChange}
                  onCardClick={handleCardClick}
                  selectedCards={selectedCards}
                  isBulkMode={isBulkMode}
                  onWantedToggle={handleWantedToggle}
                  onTradableToggle={handleTradableToggle}
                  showTradeButtons={(card) => isCardTradeable(card.rarity)}
                  className="gap-2 sm:gap-4"
                  getPathForLang={getPathForLang}
                  setInfo={set ? {
                    display_name: set.display_name,
                    display_name_en: set.display_name_en
                  } : undefined}
                />
                {isLoadingMore && (
                  <div className="flex justify-center py-6 sm:py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'missions',
      label: (
        <div className="flex items-center gap-2 px-3 py-2 text-base">
          <Trophy className="w-5 h-5" />
          <span>{t('set_detail.tabs.missions')}</span>
        </div>
      ),
      children: (
        <div className="bg-gray-50 rounded-lg rounded-tl-none shadow-sm px-2 sm:px-3 md:px-4 py-3 sm:py-4">
          {missionsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : missions.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <Trophy className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t('set_detail.missions.no_missions')}</h3>
              <p className="mt-1 text-sm text-gray-500">
                {t('set_detail.missions.no_missions_description')}
              </p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {/* Annonce AdSense dans l'onglet des missions */}
              <div className="mt-6 mb-8 flex justify-center items-center">
                <AdSense adSlot="1424794127" id="missions-top" />
              </div>
              
              {missions.map((mission) => {
                const progress = calculateProgress(mission, collection);
                return (
                  <div
                    key={mission.id}
                    onClick={() => navigate(getPathForLang(`/sets/${code}/missions/${mission.id}`))}
                    className="flex items-center gap-3 sm:gap-4 bg-white rounded-lg p-3 sm:p-4 shadow hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="flex-shrink-0">
                      <Trophy className="w-8 h-8 text-indigo-600" />
                    </div>
                    <div className="flex-grow min-w-0">
                      <div className="flex justify-between items-baseline">
                        <h3 className="font-medium text-gray-900">{mission.name}</h3>
                        <div className="text-sm text-gray-500">
                          {progress}%
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{mission.description}</p>
                      <div className="mt-2 flex items-center gap-4">
                        <div className="flex-grow">
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-indigo-600 rounded-full transition-all duration-300"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                        <Button
                          className="flex items-center gap-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(getPathForLang(`/sets/${code}/missions/${mission.id}`));
                          }}
                        >
                          {t('set_detail.missions.view_mission')}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )
    },
  ];

  // Initialize infinite scroll
  useInfiniteScroll(loadMore, hasMore && !isLoadingMore && activeTab === 'cards');
  
  useEffect(() => {
    if (set?.id) {
      loadMissions();
    }
  }, [set?.id]);

  const loadMissions = async () => {
    if (!set) return;
    
    try {
      const { data, error } = await supabase
        .from('missions')
        .select(`
          *,
          mission_card_groups (
            id,
            mission_card_variants (
              card_id,
              cards (*)
            )
          ),
          mission_rewards (
            quantity,
            rewards (*)
          )
        `)
        .eq('set_id', set.id)
        .eq('is_active', true);

      if (error) throw error;
      setMissions(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des missions:', error);
    } finally {
      setMissionsLoading(false);
    }
  };

  if (setLoading) {
    return (
      <PageLayout
        breadcrumbProps={{
          setName: t('set_detail.loading')
        }}
        className="!p-0 sm:!p-2 md:!p-4"
      >
        <SetDetailSkeleton />
      </PageLayout>
    );
  }

  if (setError || !set) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-red-500">{t('set_detail.error.title')}</p>
        </div>
      </div>
    );
  }

  if (cardsError) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-red-500">{t('set_detail.error.description')}: {cardsError}</p>
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      breadcrumbProps={{
        setName: lang === 'en' ? set.display_name_en || set.display_name : set.display_name
      }}
      className="!p-0 sm:!p-2 md:!p-4"
    >
      <div className="px-2 sm:px-4">

        
        {set?.display_name && (
          <PageTitle title={lang === 'en' ? set.display_name_en || set.display_name : set.display_name} />
        )}
        
        <div className="mt-2 sm:mt-4 bg-white rounded-xl p-2 sm:p-3 md:p-4 shadow-md">
          <div className="flex items-center gap-3 sm:gap-4">
            {set.img ? (
              <img
                src={`${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${set.img}`}
                alt={`${t('set_detail.set_symbol')} ${lang === 'en' ? set.display_name_en || set.display_name : set.display_name}`}
                className="w-16 h-16 sm:w-20 sm:h-20 object-contain flex-shrink-0"
              />
            ) : (
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Library className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 truncate">{lang === 'en' ? set.display_name_en || set.display_name : set.display_name}</h1>
              <div className="mt-1 sm:mt-2 flex items-center text-gray-500">
                <Library className="w-4 h-4 mr-1" />
                <span className="text-sm sm:text-base">{setStats?.total_cards} {t('set_detail.collection.cards')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 sm:mt-4 sm:px-4">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          className="[&_.ant-tabs-nav]:!mb-0 [&_.ant-tabs-nav::before]:!border-b-0 [&_.ant-tabs-tab]:!bg-gray-50 [&_.ant-tabs-tab-active]:!bg-white [&_.ant-tabs-nav]:!px-0 [&_.ant-tabs-nav-wrap]:!mx-0"
          tabBarStyle={{ marginBottom: 0 }}
          tabBarGutter={0}
          type="card"
          items={tabItems}
        />
      </div>

      <ConfirmModal
        isOpen={isConfirmModalVisible}
        onClose={() => setIsConfirmModalVisible(false)}
        onConfirm={handleBulkUpdate}
        title={t('set_detail.bulk_mode.confirm_title')}
        message={t('set_detail.bulk_mode.confirm_text', { count: selectedCards.size })}
        details={{
          quantity: bulkQuantity,
          selectedCount: selectedCards.size
        }}
      />

      {/* Annonce AdSense en barre latérale */}
      <div className="col-span-1 h-full">
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 sticky top-4">
          <div className="mt-6 mb-8 flex justify-center items-center">
            <AdSense adSlot="1424794127" id="sidebar" />
          </div>
        </div>
      </div>
    </PageLayout>
  );
}